import {
  BffCatalogoItem,
  BffCompania,
  BffDescripcionCobertura,
  BffEstadoEmision,
  BffItemCatalogo,
  BffParametros,
  BffPatenteResponse,
  BffPlan,
  BffProvincia,
} from './types';

/**
 * Adaptadores PUROS: convierten el shape nuevo del BFF al shape que hoy
 * consumen los componentes del cotizador de auto/moto. Sirven para que el
 * camino nuevo sea un cambio de servicio y no una reescritura de pantallas.
 *
 * Nada de estos adaptadores hace IO ni toca stores.
 */

/* ───────────────── §3.1 parámetros ───────────────── */

export interface CompaniaLegacy {
  codigoCompania: string;
  nombreCompania: string;
  emisionOnlineHabilitada: boolean;
}

export interface ParametrosLegacy {
  rubro: string;
  origen: string;
  tipoCotizador: string;
  /** El componente hace `JSON.parse(estilos)`: tiene que ser un string. */
  estilos: string | null;
  dataCarHabilitado: boolean;
  companias: CompaniaLegacy[];
  /** Extras del BFF que el camino viejo no tenía (no rompen a nadie). */
  emisionOnlineHabilitada: boolean;
  emisionOnlineMotivo?: string;
  logoUrl?: string;
  crmType?: string;
  esPartner?: boolean;
  checkBot?: boolean;
}

export const adaptCompania = (compania: BffCompania): CompaniaLegacy => ({
  codigoCompania: compania.codigo,
  nombreCompania: compania.nombre,
  emisionOnlineHabilitada: !!compania.emisionOnlineHabilitada,
});

/**
 * `GET /{ramo}/v1/parametros` -> `parametrosIniciales` del CotizacionStore.
 * `rubro` va en el shape viejo ("AUTO"/"MOTO") porque el resto del front lo
 * compara así para detectar cambio de rubro.
 */
export const adaptParametros = (
  bff: BffParametros,
  rubro: 'AUTO' | 'MOTO'
): ParametrosLegacy => ({
  rubro,
  origen: bff.origen?.codigo || '',
  tipoCotizador: bff.tipoCotizador || 'WEB',
  estilos: bff.estilos ? JSON.stringify(bff.estilos) : null,
  dataCarHabilitado: rubro === 'AUTO' ? !!bff.patenteHabilitada : false,
  companias: (bff.companias || [])
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map(adaptCompania),
  emisionOnlineHabilitada: !!bff.emisionOnline?.habilitada,
  emisionOnlineMotivo: bff.emisionOnline?.motivo,
  logoUrl: bff.logoUrl,
  crmType: bff.origen?.crmType,
  esPartner: bff.origen?.esPartner,
  checkBot: bff.checkBot,
});

/* ───────────────── descripciones de cobertura ───────────────── */

export interface DescripcionLegacy {
  /** Código de compañía; "" = la descripción aplica a todas. */
  CompaniaDescripcion: string;
  CodigoCompania: string;
  ItemDescripcion2Nombre: string;
  Texto: string;
  Aclaraciones: string | null;
}

/**
 * Descripciones de cobertura al shape viejo. `compania` puede venir null, que
 * significa "vale para todas": en ese caso `CompaniaDescripcion` queda en ""
 * y el consumidor la toma como comodín.
 */
export const adaptDescripciones = (
  descripciones: BffDescripcionCobertura[]
): DescripcionLegacy[] =>
  (descripciones || []).map((desc) => ({
    CompaniaDescripcion: desc.compania || '',
    CodigoCompania: desc.codigoCobertura,
    ItemDescripcion2Nombre: desc.titulo,
    Texto: desc.descripcion,
    Aclaraciones: desc.aclaracion ?? null,
  }));

/**
 * Descripciones que aplican a una compañía y cobertura. Las que no declaran
 * compañía valen para todas; si hay una específica de la compañía, esa gana.
 */
export const descripcionesDeCobertura = (
  descripciones: DescripcionLegacy[],
  codigoCobertura: string,
  codigoCompania?: string
): DescripcionLegacy[] => {
  const deLaCobertura = (descripciones || []).filter(
    (desc) => desc.CodigoCompania === codigoCobertura
  );
  const especificas = codigoCompania
    ? deLaCobertura.filter(
        (desc) =>
          desc.CompaniaDescripcion.toLowerCase() === codigoCompania.toLowerCase()
      )
    : [];
  const porItem = new Map<string, DescripcionLegacy>();
  deLaCobertura
    .filter((desc) => !desc.CompaniaDescripcion)
    .forEach((desc) => porItem.set(desc.ItemDescripcion2Nombre, desc));
  especificas.forEach((desc) =>
    porItem.set(desc.ItemDescripcion2Nombre, desc)
  );
  return Array.from(porItem.values());
};

/* ───────────────── §3.1 patente y catálogos de vehículo ───────────────── */

export interface PatenteLegacy {
  marca: string;
  modelo: string;
  version: string;
  anio: string;
  esAntiguo: boolean;
  /**
   * false cuando el BFF no pudo resolver alguno de los ids por nombre. La
   * pantalla de datos pasa a modo manual con los nombres precargados.
   */
  idsResueltos: boolean;
}

/**
 * `POST /auto/v1/vehiculos/patente` -> lo que devuelve hoy `Patente.jsx`:
 * el objeto `datos`, o null si el datacar no encontró el vehículo.
 */
export const adaptPatente = (bff: BffPatenteResponse): PatenteLegacy | null => {
  if (!bff?.encontrado || !bff.vehiculo) return null;
  const v = bff.vehiculo;
  return {
    marca: v.marcaNombre,
    modelo: v.modeloNombre,
    version: v.versionNombre,
    anio: String(v.anio),
    esAntiguo: !!v.esAntiguo,
    idsResueltos: !!v.marcaId && !!v.modeloId && !!v.versionId,
  };
};

/** `[{id,nombre}]` -> `["nombre", ...]`, que es lo que esperan los selects. */
export const aNombres = (items: BffItemCatalogo[]): string[] =>
  (items || []).map((item) => item.nombre);

/** `[int]` -> `["2020", ...]`. El 0 del BFF es "0 km" y se muestra así. */
export const adaptAnios = (anios: number[]): string[] =>
  (anios || []).map((anio) => (anio === 0 ? '0 km' : String(anio)));

/** Provincias: el select viejo consume un array de nombres ordenado. */
export const adaptProvincias = (provincias: BffProvincia[]): string[] =>
  (provincias || [])
    .map((provincia) => provincia.nombre)
    .sort((a, b) => a.localeCompare(b));

/** Catálogos de pago: el shape del BFF ya es `{codigo, descripcion}`. */
export const adaptCatalogo = (
  items: BffCatalogoItem[]
): Array<{ codigo: string; descripcion: string }> =>
  (items || []).map((item) => ({
    codigo: item.codigo,
    descripcion: item.descripcion,
  }));

/* ───────────────── §3.3 precios ───────────────── */

/**
 * `tier` -> columna, SOLO como respaldo: el BFF manda `columna` en cada plan y
 * esa manda. Se conserva para un plan viejo que no la traiga.
 */
export const columnaDeTier = (tier: string): string => {
  switch ((tier || '').toUpperCase()) {
    case 'RC':
      return 'A';
    case 'TC':
      return 'B';
    case 'TR':
      return 'D';
    default:
      return '';
  }
};

/** Nombre visible de la cobertura por columna (mismo texto que hoy, AR). */
export const nombreCoberturaPorColumna = (columna: string): string => {
  switch (columna) {
    case 'A':
      return 'Responsabilidad Civil';
    case 'B':
      return 'Terceros Completo';
    case 'C':
      return 'Terceros Completo Full';
    case 'D':
    case 'E':
      return 'Todo Riesgo';
    default:
      return '';
  }
};

const enPesos = (valor: number): string =>
  Math.round(valor).toLocaleString('es-AR');

export interface ItemPrecioLegacy {
  id: string;
  columna: string;
  /** Orden de aparición dentro de la compañía, tal como lo manda el BFF. */
  orden: number;
  premio: string;
  prima: number;
  franquicia: string;
  nroCotizacion: string;
  codigoCobertura: string;
  descripcionCobertura: string;
  nombreCobertura: string;
  esPrecioPromocional: boolean;
  SumaAsegurada: string;
  /** Precio tachado. "" cuando el BFF no manda `precioTachado`. */
  PrecioSinDescuento: string;
  DescuentoPorcentaje: number;
  descripcionCoberturaOriginal: string;
  descripciones: Array<{
    Nombre: string;
    Descripcion: string;
    Aclaracion: string | null;
  }>;
  DescResumen: string[];
  LogoCompania: string;
  NombreCompania: string;
  CodigoCompania: string;
  Mostrar: boolean;
  Afip: string;
  EmisionHabilitada: boolean;
  /** La compañía exige la VTV para emitir este plan (US 24094). */
  vtvRequerida: boolean;
}

export interface ContextoAdaptacionPrecio {
  codigoCompania: string;
  nombreCompania: string;
  logoCompania: string;
  afip: string;
  emisionHabilitada: boolean;
  /** Suma asegurada de la cotización, si el plan no trae la suya. */
  sumaAseguradaFallback?: number | null;
}

/**
 * Plan del BFF -> item de precios del shape viejo.
 *
 * Diferencia importante con el camino viejo: NO se infla el precio x1.2 ni se
 * hardcodea 30 % de descuento. El precio tachado sale del BFF
 * (`precioTachado`); si no viene, no hay tachado y el descuento es 0.
 */
export const adaptPreciosItem = (
  plan: BffPlan,
  ctx: ContextoAdaptacionPrecio
): ItemPrecioLegacy => {
  // La columna la manda el BFF; `tier` solo cubre un plan que no la traiga.
  const columna = plan.columna || columnaDeTier(plan.tier);
  const suma =
    plan.sumaAsegurada != null && plan.sumaAsegurada > 0
      ? plan.sumaAsegurada
      : ctx.sumaAseguradaFallback ?? 0;

  const tachado =
    plan.precioTachado != null && plan.precioTachado > plan.premioMensual
      ? plan.precioTachado
      : undefined;

  const descuento =
    tachado != null
      ? Math.round(((tachado - plan.premioMensual) / tachado) * 100)
      : 0;

  return {
    id: plan.itemId,
    columna,
    orden: plan.orden ?? 0,
    premio: enPesos(plan.premioMensual),
    prima: plan.primaAnual,
    franquicia: plan.franquicia == null ? '' : String(plan.franquicia),
    nroCotizacion: plan.nroCotizacion || '',
    codigoCobertura: plan.codigoCobertura,
    descripcionCobertura: plan.nombre,
    nombreCobertura: nombreCoberturaPorColumna(columna) || plan.nombre,
    esPrecioPromocional: !!plan.esPromocional,
    SumaAsegurada: suma ? enPesos(suma) : '',
    PrecioSinDescuento: tachado != null ? enPesos(tachado) : '',
    DescuentoPorcentaje: descuento,
    descripcionCoberturaOriginal: plan.descripcion || plan.nombre,
    // `plan.coberturas` viene como OBJETOS ({codigo, nombre, incluida, detalle}), no como
    // strings: meterlos crudos en `Nombre` hacía que React tirara "Objects are not valid as a
    // React child" al abrir el detalle y dejaba la pantalla en blanco. Se sigue aceptando la
    // forma vieja (lista de strings) para no depender del orden de deploy entre front y BFF.
    descripciones: (plan.coberturas || []).map((cobertura) =>
      typeof cobertura === 'string'
        ? { Nombre: cobertura, Descripcion: 'Si', Aclaracion: null }
        : {
            Nombre: cobertura?.nombre || cobertura?.codigo || '',
            Descripcion: cobertura?.incluida ? cobertura?.detalle || 'Si' : 'No',
            Aclaracion: cobertura?.detalle ?? null,
          }
    ),
    DescResumen: [],
    LogoCompania: ctx.logoCompania,
    NombreCompania: plan.compania?.nombre || ctx.nombreCompania,
    CodigoCompania: plan.compania?.codigo || ctx.codigoCompania,
    Mostrar: true,
    Afip: ctx.afip,
    EmisionHabilitada: ctx.emisionHabilitada,
    vtvRequerida: !!plan.vtvRequerida,
  };
};

/** Todos los planes de una compañía, en el shape viejo. */
export const adaptPrecios = (
  planes: BffPlan[],
  ctx: ContextoAdaptacionPrecio
): ItemPrecioLegacy[] =>
  (planes || [])
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map((plan) => adaptPreciosItem(plan, ctx));


/* ───────────────── §3.5 estado de la emisión ───────────────── */

export interface EstadoEmisionVista {
  estado: string;
  polizaId?: string;
  numeroPoliza?: string;
  /** Segundos hasta la próxima consulta. `null` = no hay reintento útil. */
  esperaSegundos: number | null;
  /** La póliza quedó emitida: se puede ir a la confirmación con documentos. */
  emitida: boolean;
  /** La emisión falló y se puede volver a intentar. */
  fallida: boolean;
  /** Sigue en curso: se sigue consultando el estado. */
  enCurso: boolean;
  /**
   * La emisión quedó colgada y nadie puede confirmarla desde el front (§3.5).
   * La pantalla avisa que estamos confirmando la póliza y NO ofrece reintento:
   * un segundo intento arriesga un segundo cobro.
   */
  requiereAsistencia: boolean;
}

/**
 * `GET cotizaciones/{id}/emision` -> lo que necesita la UI para decidir qué
 * mostrar. `requiereAsistencia` llega explícito del BFF; también se deduce de
 * un `issuing` con `retryAfterSeconds: null`, que es la misma situación.
 */
export const adaptEstadoEmision = (
  bff: BffEstadoEmision | null | undefined
): EstadoEmisionVista => {
  const estado = bff?.estado || 'none';
  const espera =
    bff?.retryAfterSeconds === undefined || bff?.retryAfterSeconds === null
      ? null
      : bff.retryAfterSeconds;
  const enCurso = estado === 'issuing';
  // `null` explícito = el BFF dice que no hay reintento útil. Un campo AUSENTE
  // es sólo "no sé cuánto esperar", y ahí se sigue consultando.
  const sinReintento = bff?.retryAfterSeconds === null;
  const requiereAsistencia = !!bff?.requiereAsistencia || (enCurso && sinReintento);

  return {
    estado,
    polizaId: bff?.polizaId,
    numeroPoliza: bff?.numeroPoliza,
    esperaSegundos: espera,
    emitida: estado === 'issued',
    fallida: estado === 'failed',
    enCurso,
    requiereAsistencia,
  };
};
